//
// Analytics Controller
//

declare global {
  interface Window {
    plausible?: (eventName: string, options?: { props?: { [key: string]: any } }) => void
  }
}

export default class AnalyticsController {
  public static readonly shared = new AnalyticsController()

  // MARK: - Object Lifecycle

  private constructor() {
    document.addEventListener('click', this.onClick.bind(this))
    document.addEventListener('submit', this.onSubmit.bind(this))
  }

  // MARK: - Events

  private onClick(event: Event) {
    const element = event.target
    if (element instanceof HTMLElement === false) return

    if (element.matches('.add_to_cart_button')) {
      this.onButtonAddToCart(element)
    }
  }

  private onSubmit(event: Event) {
    const element = event.target
    if (element instanceof HTMLFormElement === false) return

    if (element.matches('.product .summary .cart')) {
      this.onFormAddToCart(element)
    } else if (element.matches('.routine__form')) {
      this.onRoutineFormAddToCart(element)
    }
  }

  // MARK: - Plausible

  private trackPlausibleEvent(eventName: string, eventData?: { [key: string]: any }) {
    if (typeof window.plausible !== 'function') return

    window.plausible(eventName, { props: eventData })
  }

  // MARK: - Add To Cart

  private onButtonAddToCart(element: HTMLElement) {
    const id = parseInt(element.getAttribute('data-product_id') || '')
    if (isNaN(id) || id <= 0) return

    const currency = element.getAttribute('data-product_currency')
    if (!currency) return

    const quantity = parseFloat(element.getAttribute('data-quantity') || '')
    if (isNaN(quantity) || quantity <= 0) return

    const category = element.getAttribute('data-product_category') || undefined

    let name = undefined
    let price = undefined

    const productElement = element.closest('.product')
    if (productElement instanceof HTMLElement) {
      const titleElement = productElement.querySelector('.woocommerce-loop-product__title')
      if (titleElement instanceof HTMLElement) {
        name = titleElement.textContent.trim()
      }

      let priceElement = productElement.querySelector('.price')
      if (priceElement instanceof HTMLElement) {
        const insertElement = priceElement.querySelector('ins')
        if (insertElement instanceof HTMLElement) {
          priceElement = insertElement
        }

        const amountElement = priceElement.querySelector('.woocommerce-Price-amount')
        if (amountElement instanceof HTMLElement) {
          price = this.parsePrice(amountElement.textContent)
        }
      }
    }

    if (typeof price !== 'number' || isNaN(price) || price < 0) return

    const value = parseFloat((price * quantity).toFixed(2))

    this.trackPlausibleEvent('Add to Cart', {
      'Product ID': id,
      'Product Name': name,
      'Product Category': category,
      'Product Price': price,
      'Product Quantity': quantity,
      'Total Value': value,
      Currency: currency
    })
  }

  private onFormAddToCart(element: HTMLFormElement) {
    const submitElement = element.querySelector('[type="submit"][name="add-to-cart"]')
    if (submitElement instanceof HTMLButtonElement === false && submitElement instanceof HTMLInputElement === false)
      return

    const id = parseInt(submitElement.value)
    if (isNaN(id) || id <= 0) return

    let category = undefined
    let name = undefined
    let price = undefined
    let currency = undefined
    let quantity = undefined

    const categoryElement = element.querySelector('input[name="product_category"]')
    if (categoryElement instanceof HTMLInputElement) {
      category = categoryElement.value || undefined
    }

    const summaryElement = element.closest('.product .summary')
    if (summaryElement instanceof HTMLElement) {
      const titleElement = summaryElement.querySelector(':scope > .product_title')
      if (titleElement instanceof HTMLElement) {
        name = titleElement.textContent.trim()
      }

      let priceElement = element.querySelector('.woocommerce-variation-price')
      if (priceElement instanceof HTMLElement === false) {
        priceElement = summaryElement.querySelector(':scope > .price')
      }

      if (priceElement instanceof HTMLElement) {
        const insertElement = priceElement.querySelector('ins')
        if (insertElement instanceof HTMLElement) {
          priceElement = insertElement
        }

        const amountElement = priceElement.querySelector('.woocommerce-Price-amount')
        if (amountElement instanceof HTMLElement) {
          price = this.parsePrice(amountElement.textContent)
        }
      }
    }

    const currencyElement = element.querySelector('input[name="product_currency"]')
    if (currencyElement instanceof HTMLInputElement) {
      currency = currencyElement.value || undefined
    }

    const quantityElement = element.querySelector('input[name="quantity"]')
    if (quantityElement instanceof HTMLInputElement) {
      const quantityElementValue = parseInt(quantityElement.value)
      if (quantityElementValue > 0) {
        quantity = quantityElementValue
      }
    }

    if (typeof price !== 'number' || isNaN(price) || price < 0) return
    if (typeof currency !== 'string' || currency.length <= 0) return
    if (typeof quantity !== 'number' || isNaN(quantity) || quantity <= 0) return

    const value = parseFloat((price * quantity).toFixed(2))

    this.trackPlausibleEvent('Add to Cart', {
      'Product ID': id,
      'Product Name': name,
      'Product Category': category,
      'Product Price': price,
      'Product Quantity': quantity,
      'Total Value': value,
      Currency: currency
    })
  }

  private onRoutineFormAddToCart(element: HTMLFormElement) {
    const routineElement = element.closest('.routine')
    if (routineElement instanceof HTMLElement === false) return

    const id = parseFloat(routineElement.getAttribute('data-bundle-product-id') || '')
    if (isNaN(id) || id <= 0) return

    const currency = routineElement.getAttribute('data-bundle-currency') || undefined
    if (!currency) return

    const price = parseFloat(routineElement.getAttribute('data-total-price') || '')
    if (isNaN(price) || price < 0) return

    const name = routineElement.getAttribute('data-bundle-name') || undefined

    this.trackPlausibleEvent('Add to Cart', {
      'Product ID': id,
      'Product Name': name,
      'Product Price': price,
      'Product Quantity': 1,
      'Total Value': price,
      Currency: currency
    })
  }

  // MARK: - Formatters

  private parsePrice(value: string): number {
    let price = value.replace(/[^\d\s\.,]/g, '').trim()
    const lastCommaIndex = price.lastIndexOf(',')
    const lastPeriodIndex = price.lastIndexOf('.')

    if (lastCommaIndex > lastPeriodIndex) {
      price = price.replace(/\./g, '').replace(',', '.')
    } else if (lastPeriodIndex > lastCommaIndex) {
      price = price.replace(/,/g, '')
    } else if (lastCommaIndex !== -1 || lastPeriodIndex !== -1) {
      const separator = lastCommaIndex > -1 ? ',' : '.'
      const parts = price.split(separator)

      if (parts[parts.length - 1].length <= 2) {
        price = price.replace(separator, '.')
      } else {
        price = price.replace(separator, '')
      }
    }

    price = price.replace(/\s/g, '')
    return parseFloat(price)
  }
}
